import { ChecklistItemInputDefinition, ChecklistTemplateSection, Clientize, ChecklistTemplateDocument } from 'types';

import { api } from './api';

interface ChecklistTemplateListResponse {
  data: Clientize<ChecklistTemplateDocument>[];
  total: number;
}

interface ListChecklistTemplatesParams {
  isCurrent?: boolean;
  queueType?: string;
  programType?: string;
  templateGroupId?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

type TemplateItemPayload = {
  key: string;
  name: string;
  description: string;
  order: number;
  sectionKey?: string;
  conditions?: Array<{ name: string; params?: string[] }>;
  inputs?: ChecklistItemInputDefinition[];
  documentTypes?: string[];
};

interface CreateChecklistTemplateRequest {
  name: string;
  description: string;
  programTypes: string[];
  queueType?: string;
  items: TemplateItemPayload[];
  sections?: ChecklistTemplateSection[];
}

interface SaveChecklistTemplateDraftBody {
  name?: string;
  description?: string;
  programTypes?: string[];
  items?: TemplateItemPayload[];
  sections?: ChecklistTemplateSection[];
  draftNotes?: string;
}

export const checklistTemplatesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getChecklistTemplates: build.query<ChecklistTemplateListResponse, ListChecklistTemplatesParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.isCurrent !== undefined) searchParams.set('isCurrent', String(params.isCurrent));
        if (params.queueType) searchParams.set('queueType', params.queueType);
        if (params.programType) searchParams.set('programType', params.programType);
        if (params.templateGroupId) searchParams.set('templateGroupId', params.templateGroupId);
        if (params.status) searchParams.set('status', params.status);
        if (params.limit !== undefined) searchParams.set('limit', String(params.limit));
        if (params.offset !== undefined) searchParams.set('offset', String(params.offset));
        const qs = searchParams.toString();
        return { url: `/checklist-templates${qs ? `?${qs}` : ''}` };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'ChecklistTemplates' as const, id })),
              { type: 'ChecklistTemplates', id: 'LIST' },
            ]
          : [{ type: 'ChecklistTemplates', id: 'LIST' }],
    }),

    getChecklistTemplate: build.query<Clientize<ChecklistTemplateDocument>, string>({
      query: (id) => ({ url: `/checklist-templates/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'ChecklistTemplates', id }],
    }),

    createChecklistTemplate: build.mutation<Clientize<ChecklistTemplateDocument>, CreateChecklistTemplateRequest>({
      query: (body) => ({
        url: '/checklist-templates',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ChecklistTemplates', id: 'LIST' }],
    }),

    saveChecklistTemplateDraft: build.mutation<
      Clientize<ChecklistTemplateDocument>,
      { id: string; data: SaveChecklistTemplateDraftBody }
    >({
      query: ({ id, data }) => ({
        url: `/checklist-templates/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ChecklistTemplates', id },
        { type: 'ChecklistTemplates', id: 'LIST' },
      ],
    }),

    getOrCreateChecklistTemplateDraft: build.mutation<Clientize<ChecklistTemplateDocument>, string>({
      query: (id) => ({
        url: `/checklist-templates/${id}/draft`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'ChecklistTemplates', id }],
    }),

    publishChecklistTemplateDraft: build.mutation<
      Clientize<ChecklistTemplateDocument>,
      { id: string; draftNotes: string }
    >({
      query: ({ id, draftNotes }) => ({
        url: `/checklist-templates/${id}/publish`,
        method: 'POST',
        body: { draftNotes },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ChecklistTemplates', id },
        { type: 'ChecklistTemplates', id: 'LIST' },
      ],
    }),

    discardChecklistTemplateDraft: build.mutation<void, string>({
      query: (id) => ({
        url: `/checklist-templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'ChecklistTemplates', id },
        { type: 'ChecklistTemplates', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetChecklistTemplatesQuery,
  useLazyGetChecklistTemplatesQuery,
  useGetChecklistTemplateQuery,
  useCreateChecklistTemplateMutation,
  useSaveChecklistTemplateDraftMutation,
  useGetOrCreateChecklistTemplateDraftMutation,
  usePublishChecklistTemplateDraftMutation,
  useDiscardChecklistTemplateDraftMutation,
} = checklistTemplatesApi;
